import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAllArticles } from "@/lib/useAllArticles";
import { renderText, cleanText } from "@/lib/articleText";
import { ARTICLE_GROUPS } from "@/lib/seoArticles";
import {
    Library as LibraryIcon, Search, ArrowRight, Star, Clock, Filter,
    BookOpen, ChevronDown, ChevronRight,
} from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

/**
 * <KnowledgeLibrarySection /> — the caregiver-facing browse experience
 * for every published article (static SEO + Studio-published).
 *
 * Rendered from BOTH the standalone /library route and inside the
 * /education ("Learn") page so caregivers find content from wherever
 * they're exploring.
 *
 * Layout choice ("category headers + dropdown filter"):
 *   - Articles always render grouped under collapsible category
 *     headings so a 90+ article library is scannable on first paint.
 *   - The dropdown filter then narrows further if the caregiver
 *     already knows what they want.
 *   - When ANY search/filter is active, all groups auto-expand so
 *     matches aren't hidden behind a collapsed header.
 *
 * Props:
 *   • showHeader (bool)     — render the page-level title/intro card.
 *                             Pass false when embedded under another
 *                             heading (e.g. inside the Learn page).
 *   • articles (optional)   — override the merged article list. When
 *                             omitted we use useAllArticles().
 *   • defaultCollapsed (bool) — render group headers collapsed by
 *                             default. Pass true to make the section
 *                             feel like a TOC (used in /education).
 *   • testidPrefix (string) — namespace for data-testid attributes so
 *                             two instances on the same page don't
 *                             collide.
 */

const STAGE_PILLS = [
    { value: "", label: "All stages" },
    { value: "early", label: "Early" },
    { value: "middle", label: "Middle" },
    { value: "late", label: "Late" },
    { value: "all", label: "All stages tag" },
];

const SORT_OPTIONS = [
    { value: "newest", label: "Newest" },
    { value: "recently_updated", label: "Recently updated" },
    { value: "most_viewed", label: "Most viewed" },
];

function readingTime(article) {
    let text = (article.summary || "") + " ";
    for (const b of article.body || []) {
        if (b?.text) text += b.text + " ";
        if (Array.isArray(b?.items)) text += b.items.join(" ") + " ";
    }
    const words = (text.trim().match(/\S+/g) || []).length;
    return Math.max(2, Math.round(words / 220));
}

function categoryLabel(article) {
    if (article.category) return article.category;
    return ARTICLE_GROUPS[article.group]?.label || article.group || "Caregiver Guides";
}

function stageBadgeClass(stage) {
    return ({
        early: "bg-stage_early/20 text-stage_early-dark",
        middle: "bg-stage_middle/25 text-stage_middle-dark",
        late: "bg-stage_late/30 text-stage_late-dark",
    }[stage]) || "bg-cream-light text-[#4C5551]";
}

function stageLabel(stage) {
    return ({ early: "Early stage", middle: "Middle stage", late: "Late stage", all: "All stages" }[stage]) || "All stages";
}

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
}

export function ArticleCard({ article, featured = false, testidPrefix = "library" }) {
    const path = `/${article.group}/${article.slug}`;
    const onClick = () => track(EVENTS.RESOURCE_CLICK, {
        resource_category: categoryLabel(article),
        resource_type: `${testidPrefix}_card`,
        slug: article.slug,
    });
    return (
        <Link
            to={path}
            onClick={onClick}
            data-testid={`${testidPrefix}-card-${article.slug}`}
            className={`ml-card ml-card-hover p-5 sm:p-6 flex flex-col gap-3 group ${featured ? "border-2 border-teal-light/70 bg-gradient-to-br from-cream-light to-white" : ""}`}
        >
            <div className="flex items-start justify-between gap-3">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark">
                    {categoryLabel(article)}
                </span>
                {featured && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-coral-dark">
                        <Star size={11} strokeWidth={2} /> Featured
                    </span>
                )}
            </div>
            <h3 className="font-heading text-lg sm:text-xl text-[#1F2624] leading-snug">
                {cleanText(article.h1 || article.title)}
            </h3>
            {article.summary && (
                <p className="text-sm text-[#4C5551] leading-relaxed line-clamp-3">
                    {renderText(article.summary, null, `lib-${article.slug}`)}
                </p>
            )}
            <div className="mt-auto flex items-center justify-between gap-2 pt-2 border-t border-cream-dark/50">
                <div className="flex items-center gap-2 flex-wrap">
                    {article.stage && (
                        <span className={`text-[10px] uppercase tracking-wider font-semibold rounded-2xl px-2 py-0.5 ${stageBadgeClass(article.stage)}`}>
                            {stageLabel(article.stage)}
                        </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#6E7A74]">
                        <Clock size={11} strokeWidth={2} /> {readingTime(article)} min read
                    </span>
                </div>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-teal-dark group-hover:gap-2 transition-all">
                    Read guide <ArrowRight size={14} strokeWidth={2} />
                </span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-[#6E7A74] -mt-1">
                <span>Reviewed by Ashlee Skabla Velez, APRN</span>
                {article.updated_at && (
                    <span>Updated {formatDate(article.updated_at)}</span>
                )}
            </div>
        </Link>
    );
}

export default function KnowledgeLibrarySection({
    showHeader = true,
    articles: articlesProp,
    defaultCollapsed = false,
    testidPrefix = "library",
}) {
    const hookArticles = useAllArticles();
    const all = articlesProp ?? hookArticles;

    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [stage, setStage] = useState("");
    const [sort, setSort] = useState("newest");

    // Collapse state for each category header — persisted per-prefix so
    // refreshing /education vs /library doesn't share collapse state.
    const collapsedKey = `ml_lib_collapsed_${testidPrefix}`;
    const [collapsed, setCollapsed] = useState(() => {
        if (typeof window === "undefined") return {};
        try {
            const raw = window.localStorage.getItem(collapsedKey);
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    });
    useEffect(() => {
        if (typeof window === "undefined") return;
        try { window.localStorage.setItem(collapsedKey, JSON.stringify(collapsed)); }
        catch { /* private mode */ }
    }, [collapsed, collapsedKey]);

    const isAnyFilterActive = !!(search.trim() || category || stage);

    const categories = useMemo(() => {
        const set = new Set();
        for (const a of all) set.add(categoryLabel(a));
        return Array.from(set).filter(Boolean).sort();
    }, [all]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = all.filter((a) => {
            if (category && categoryLabel(a) !== category) return false;
            if (stage && (a.stage || "all") !== stage) return false;
            if (q) {
                const hay = `${a.h1 || ""} ${a.title || ""} ${a.summary || ""} ${(a.tags || []).join(" ")} ${categoryLabel(a)}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
        if (sort === "newest") {
            list = list.sort((a, b) => (b.published_at || b.updated_at || "").localeCompare(a.published_at || a.updated_at || ""));
        } else if (sort === "recently_updated") {
            list = list.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        } else if (sort === "most_viewed") {
            list = list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
        }
        return list;
    }, [all, search, category, stage, sort]);

    // Featured cards float above the grouped grid only when no
    // filters/search are active — otherwise we want the matched cards
    // in their normal category buckets so the count is clear.
    const featured = useMemo(() => {
        if (isAnyFilterActive) return [];
        return filtered
            .filter((a) => typeof a.featured_order === "number")
            .sort((a, b) => a.featured_order - b.featured_order)
            .slice(0, 3);
    }, [filtered, isAnyFilterActive]);
    const featuredKeys = useMemo(
        () => new Set(featured.map((a) => `${a.group}/${a.slug}`)),
        [featured],
    );

    // Group the (filtered) main list by category. Output preserves
    // alphabetical category order with a stable secondary sort by the
    // active sort field already applied.
    const grouped = useMemo(() => {
        const map = new Map();
        for (const a of filtered) {
            if (featuredKeys.has(`${a.group}/${a.slug}`)) continue;
            const cat = categoryLabel(a);
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(a);
        }
        return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
    }, [filtered, featuredKeys]);

    const isCollapsed = (cat) => {
        // If a filter/search is active, never hide matches behind a
        // collapsed header — auto-expand for visibility.
        if (isAnyFilterActive) return false;
        if (collapsed[cat] !== undefined) return collapsed[cat];
        return defaultCollapsed;
    };
    const toggleCategory = (cat) => {
        setCollapsed((m) => ({ ...m, [cat]: !isCollapsed(cat) }));
    };
    const expandAll = () => {
        const m = {};
        for (const c of categories) m[c] = false;
        setCollapsed(m);
    };
    const collapseAll = () => {
        const m = {};
        for (const c of categories) m[c] = true;
        setCollapsed(m);
    };

    const isEmpty = filtered.length === 0;
    const isSearchEmpty = isEmpty && isAnyFilterActive;

    return (
        <section data-testid={`${testidPrefix}-section`}>
            {showHeader && (
                <header className="flex items-start gap-3 mb-6" data-testid={`${testidPrefix}-header`}>
                    <span className="w-12 h-12 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                        <LibraryIcon size={20} strokeWidth={1.8} />
                    </span>
                    <div>
                        <h2 className="font-heading text-2xl sm:text-3xl text-[#12776F]" data-testid={`${testidPrefix}-h1`}>
                            Knowledge Library
                        </h2>
                        <p className="text-sm sm:text-base text-[#4C5551] mt-1 max-w-2xl">
                            Evidence-informed guides for every stage of dementia caregiving. Reviewed by a board-certified Nurse Practitioner and updated as policy changes.
                        </p>
                    </div>
                </header>
            )}

            {/* Search + filter bar */}
            <div
                className="ml-card p-3 sm:p-4 mb-5 flex flex-col sm:flex-row gap-2 sticky top-2 z-10 bg-white"
                data-testid={`${testidPrefix}-filter-bar`}
            >
                <label className="flex-1 relative">
                    <Search size={16} strokeWidth={1.8} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7A74]" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search guides — try 'respite', 'sundowning', 'Medicare'…"
                        className="w-full pl-9 pr-3 py-2 rounded-xl bg-cream-light text-sm focus:outline-none focus:ring-2 focus:ring-teal"
                        data-testid={`${testidPrefix}-search-input`}
                    />
                </label>
                <div className="flex gap-2 flex-wrap">
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="px-3 py-2 rounded-xl bg-cream-light text-sm focus:outline-none focus:ring-2 focus:ring-teal"
                        data-testid={`${testidPrefix}-category-select`}
                    >
                        <option value="">All categories</option>
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select
                        value={stage}
                        onChange={(e) => setStage(e.target.value)}
                        className="px-3 py-2 rounded-xl bg-cream-light text-sm focus:outline-none focus:ring-2 focus:ring-teal"
                        data-testid={`${testidPrefix}-stage-select`}
                    >
                        {STAGE_PILLS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="px-3 py-2 rounded-xl bg-cream-light text-sm focus:outline-none focus:ring-2 focus:ring-teal"
                        data-testid={`${testidPrefix}-sort-select`}
                    >
                        {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
            </div>

            {/* Featured (only when no filter active) */}
            {featured.length > 0 && (
                <section className="mb-7" data-testid={`${testidPrefix}-featured-section`}>
                    <div className="flex items-center gap-2 mb-3">
                        <Star size={16} strokeWidth={2} className="text-coral-dark" />
                        <h3 className="font-heading text-lg text-[#1F2624]">Featured guides</h3>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {featured.map((a) => (
                            <ArticleCard
                                key={`feat-${a.group}-${a.slug}`}
                                article={a}
                                featured
                                testidPrefix={testidPrefix}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* Empty states */}
            {isEmpty && (
                <div className="ml-card p-10 text-center" data-testid={`${testidPrefix}-empty`}>
                    <BookOpen size={32} strokeWidth={1.4} className="mx-auto text-[#6E7A74] mb-3" />
                    <h3 className="font-heading text-lg text-[#1F2624]">
                        {isSearchEmpty ? "No matching guides found." : "No guides published yet."}
                    </h3>
                    <p className="text-sm text-[#4C5551] mt-1">
                        {isSearchEmpty
                            ? "Try a different search term or clear the filters."
                            : "Check back soon — our team adds new guides weekly."}
                    </p>
                    {isSearchEmpty && (
                        <button
                            onClick={() => { setSearch(""); setCategory(""); setStage(""); }}
                            className="ml-btn-secondary mt-4"
                            data-testid={`${testidPrefix}-clear-filters`}
                        >
                            <Filter size={14} strokeWidth={2} /> Clear filters
                        </button>
                    )}
                </div>
            )}

            {/* Grouped grid — collapsible category headers */}
            {grouped.length > 0 && (
                <>
                    <div
                        className="flex items-center justify-between mb-3"
                        data-testid={`${testidPrefix}-grouped-toolbar`}
                    >
                        <h3 className="font-heading text-lg text-[#1F2624]">
                            Browse by topic
                        </h3>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={expandAll}
                                className="text-xs font-medium text-teal-dark hover:underline"
                                data-testid={`${testidPrefix}-expand-all`}
                            >
                                Expand all
                            </button>
                            <button
                                type="button"
                                onClick={collapseAll}
                                className="text-xs font-medium text-[#4C5551] hover:underline"
                                data-testid={`${testidPrefix}-collapse-all`}
                            >
                                Collapse all
                            </button>
                            <span
                                className="text-xs text-[#6E7A74]"
                                data-testid={`${testidPrefix}-result-count`}
                            >
                                {filtered.length} of {all.length}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {grouped.map(([cat, items]) => {
                            const collapsedNow = isCollapsed(cat);
                            return (
                                <div
                                    key={cat}
                                    className="ml-card overflow-hidden"
                                    data-testid={`${testidPrefix}-group-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleCategory(cat)}
                                        className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 text-left hover:bg-cream-light/60"
                                        aria-expanded={!collapsedNow}
                                        data-testid={`${testidPrefix}-group-toggle-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            {collapsedNow ? (
                                                <ChevronRight size={16} strokeWidth={2} className="text-[#4C5551]" />
                                            ) : (
                                                <ChevronDown size={16} strokeWidth={2} className="text-teal-dark" />
                                            )}
                                            <span className="font-heading text-base sm:text-lg text-[#1F2624]">
                                                {cat}
                                            </span>
                                            <span className="text-xs text-[#6E7A74] bg-cream-light rounded-2xl px-2 py-0.5">
                                                {items.length}
                                            </span>
                                        </div>
                                    </button>
                                    {!collapsedNow && (
                                        <div className="border-t border-cream-dark/40 p-3 sm:p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 bg-cream-light/30">
                                            {items.map((a) => (
                                                <ArticleCard
                                                    key={`${a.group}-${a.slug}`}
                                                    article={a}
                                                    testidPrefix={testidPrefix}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </section>
    );
}
